/**
 * Tour Guide Helper / Utility
 * Manages tour guide state and user preferences
 * Can be easily extended to use API calls instead of localStorage
 */

const TOUR_STORAGE_KEYS = {
	TOUR_COMPLETED: 'tour_completed',
	TOUR_DISMISSED: 'tour_dismissed',
	TOUR_SHOWN_COUNT: 'tour_shown_count',
};

let shouldPersistTourCompletion = false;
let tourCompletionHandler = null;

/**
 * Check if the user is new and should see the tour guide
 * Returns true unless tour was permanently completed
 * Dismissed tours show again on page reload or re-login
 * @param {boolean} isAPIEnabled - Set to true to use API instead of localStorage
 * @returns {boolean} - Returns true if tour should be shown
 */
export const shouldShowTourGuide = (isAPIEnabled = false) => {
	// Only check if tour was permanently completed
	// Dismissed tours will show again on reload/re-login
	const tourCompleted = localStorage.getItem(TOUR_STORAGE_KEYS.TOUR_COMPLETED) === 'true';
	return !tourCompleted;
};

/**
 * Mark tour as completed
 * @param {boolean} isAPIEnabled - Set to true to use API instead of localStorage
 */
export const markTourAsCompleted = async (_id = 1) => {
	allowTourCompletionPersistence();
	await triggerTourCompletionHandler();
	shouldPersistTourCompletion = true;
	localStorage.setItem(TOUR_STORAGE_KEYS.TOUR_COMPLETED, 'true');
};

export const allowTourCompletionPersistence = () => {
	shouldPersistTourCompletion = true;
};

export const resetTourCompletionPersistence = () => {
	shouldPersistTourCompletion = false;
};

export const consumeTourCompletionPersistence = () => {
	const shouldPersist = shouldPersistTourCompletion;
	shouldPersistTourCompletion = false;
	return shouldPersist;
};

export const setTourCompletionHandler = (handler) => {
	tourCompletionHandler = typeof handler === 'function' ? handler : null;
};

export const clearTourCompletionHandler = () => {
	tourCompletionHandler = null;
};

export const triggerTourCompletionHandler = async () => {
	if (typeof tourCompletionHandler !== 'function') return false;
	try {
		await tourCompletionHandler();
		return true;
	} catch (error) {
		console.error('Failed to run tour completion handler:', error);
		return false;
	}
};

/**
 * Mark tour as dismissed
 * @param {boolean} isAPIEnabled - Set to true to use API instead of localStorage
 */
export const markTourAsDismissed = async (isAPIEnabled = false) => {
	if (isAPIEnabled) {
		// TODO: Replace with API call when backend is ready
		// await apiService({
		//   action: 'dismiss_tour',
		//   timestamp: Date.now()
		// });
	}
	localStorage.setItem(TOUR_STORAGE_KEYS.TOUR_DISMISSED, 'true');
};

/**
 * Reset tour guide (for testing/development)
 * Shows tour again from the beginning
 */
export const resetTourGuide = () => {
	localStorage.removeItem(TOUR_STORAGE_KEYS.TOUR_COMPLETED);
	localStorage.removeItem(TOUR_STORAGE_KEYS.TOUR_DISMISSED);
	localStorage.removeItem(TOUR_STORAGE_KEYS.TOUR_SHOWN_COUNT);
	console.log('Tour guide reset. Will show on next page load.');
};

/**
 * Get tour guide status
 * @returns {object} - Returns tour status information
 */
export const getTourGuideStatus = () => {
	return {
		isCompleted: localStorage.getItem(TOUR_STORAGE_KEYS.TOUR_COMPLETED) === 'true',
		isDismissed: localStorage.getItem(TOUR_STORAGE_KEYS.TOUR_DISMISSED) === 'true',
		shownCount: parseInt(localStorage.getItem(TOUR_STORAGE_KEYS.TOUR_SHOWN_COUNT) || '0', 10),
	};
};

/**
 * Increment tour shown count
 * Useful for analytics and deciding when to show tour again
 */
export const incrementTourShownCount = () => {
	const currentCount = parseInt(localStorage.getItem(TOUR_STORAGE_KEYS.TOUR_SHOWN_COUNT) || '0', 10);
	localStorage.setItem(TOUR_STORAGE_KEYS.TOUR_SHOWN_COUNT, String(currentCount + 1));
};

export default {
	shouldShowTourGuide,
	markTourAsCompleted,
	allowTourCompletionPersistence,
	resetTourCompletionPersistence,
	consumeTourCompletionPersistence,
	setTourCompletionHandler,
	clearTourCompletionHandler,
	triggerTourCompletionHandler,
	markTourAsDismissed,
	resetTourGuide,
	getTourGuideStatus,
	incrementTourShownCount,
};
